/**
 * Lightweight Lottie player loader for the PiA robot avatar.
 * Injects the bundled `lottie-light.min.js` script tag once (it's not an npm
 * dependency — same file used by the public-website PSA AI Assistant plugin,
 * served as a static asset so it isn't pulled into the app's JS bundle) and
 * caches the loading promise so repeated mounts (panel open/close, route
 * changes) don't reload the script. Mirrors the pattern in ./googleMaps.js.
 */

const SCRIPT_ID = "psa-lottie-light-js-sdk";

let loadPromise = null;

/**
 * @returns {Promise<typeof window.lottie>} resolves once window.lottie is ready.
 */
export const loadLottieLibrary = () => {
  if (window.lottie) return Promise.resolve(window.lottie);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.lottie));
      existing.addEventListener("error", () => reject(new Error("Failed to load Lottie player.")));
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `${process.env.PUBLIC_URL}/ai-robot/lottie-light.min.js`;
    script.async = true;
    script.onload = () => resolve(window.lottie);
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load Lottie player."));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
};
